import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { StrategyKPI } from '@/types';
import { STRATEGY_CHART_COLORS, filterAcquisitionKpis, strategyChartTooltipProps, truncateChartLabel } from './strategy-chart-shared';

export interface StrategyKpiComparisonBarsProps {
  kpis: StrategyKPI[];
  compact?: boolean;
}

export function StrategyKpiComparisonBars({ kpis, compact }: StrategyKpiComparisonBarsProps) {
  const numeric = filterAcquisitionKpis(kpis).filter(
    (k) => typeof k.target === 'number' && Number.isFinite(k.target) && k.target > 0
  );
  if (!numeric.length) return null;

  const withCurrent = numeric.filter((k) => k.current != null && Number.isFinite(k.current as number));
  const targetOnly = numeric.filter((k) => k.current == null || !Number.isFinite(k.current as number));

  const istSollData = withCurrent.map((k) => ({
    name: truncateChartLabel(k.name, compact ? 18 : 26),
    nameFull: k.name,
    unit: k.unit,
    Ist: Math.max(0, k.current as number),
    Soll: k.target,
  }));

  const sollData = targetOnly.map((k) => ({
    name: truncateChartLabel(k.name, compact ? 18 : 26),
    nameFull: k.name,
    unit: k.unit,
    Soll: k.target,
  }));

  const tooltipFmt = (value: number, name: string, item: { payload?: { unit?: string } }) => {
    const u = item.payload?.unit ? ` ${item.payload.unit}` : '';
    return [`${value}${u}`, name];
  };

  const hIst = compact ? Math.max(140, withCurrent.length * 36) : Math.max(180, withCurrent.length * 44);
  const hSoll = compact ? Math.max(120, targetOnly.length * 32) : Math.max(160, targetOnly.length * 40);

  return (
    <div className="w-full space-y-4">
      {withCurrent.length > 0 && (
        <div style={{ height: hIst }}>
          <p className="mb-1 text-xs font-medium text-muted">
            KPIs: Ist vs. Soll (Schwerpunkt Akquisition, falls passend)
          </p>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={istSollData}
              margin={
                compact
                  ? { top: 4, right: 8, left: 4, bottom: 4 }
                  : { top: 8, right: 16, left: 8, bottom: 8 }
              }
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-primary-foreground dark:stroke-muted" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: compact ? 10 : 11 }} className="fill-muted " />
              <YAxis
                type="category"
                dataKey="name"
                width={compact ? 100 : 140}
                tick={{ fontSize: compact ? 9 : 11 }}
                className="fill-muted "
              />
              <Tooltip
                {...strategyChartTooltipProps}
                formatter={(value: number, name: string, item) => tooltipFmt(value, name, item as { payload?: { unit?: string } })}
                labelFormatter={(_, payload) => {
                  const row = payload?.[0]?.payload as { nameFull?: string } | undefined;
                  return row?.nameFull ?? '';
                }}
              />
              <Legend wrapperStyle={{ fontSize: compact ? 10 : 12 }} />
              <Bar dataKey="Ist" name="Ist" fill={STRATEGY_CHART_COLORS[0]} radius={[0, 4, 4, 0]} maxBarSize={compact ? 12 : 18} />
              <Bar dataKey="Soll" name="Soll" fill={STRATEGY_CHART_COLORS[2]} radius={[0, 4, 4, 0]} maxBarSize={compact ? 12 : 18} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {targetOnly.length > 0 && (
        <div style={{ height: hSoll }}>
          <p className="mb-1 text-xs font-medium text-muted">Zielwerte (noch ohne Ist)</p>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={sollData}
              margin={
                compact
                  ? { top: 4, right: 8, left: 4, bottom: 4 }
                  : { top: 8, right: 16, left: 8, bottom: 8 }
              }
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-primary-foreground dark:stroke-muted" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: compact ? 10 : 11 }} className="fill-muted " />
              <YAxis
                type="category"
                dataKey="name"
                width={compact ? 100 : 140}
                tick={{ fontSize: compact ? 9 : 11 }}
                className="fill-muted "
              />
              <Tooltip
                {...strategyChartTooltipProps}
                formatter={(value: number, name: string, item) => tooltipFmt(value, name, item as { payload?: { unit?: string } })}
                labelFormatter={(_, payload) => {
                  const row = payload?.[0]?.payload as { nameFull?: string } | undefined;
                  return row?.nameFull ?? '';
                }}
              />
              <Bar dataKey="Soll" name="Soll" fill={STRATEGY_CHART_COLORS[1]} radius={[0, 4, 4, 0]} maxBarSize={compact ? 12 : 18} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
